import { BrandHeader } from '../../components/BrandHeader';
import { CardGrid } from '../../components/CardGrid';
import { FeaturedBanner } from '../../components/FeaturedBanner';
import { ItemCard } from '../../components/ItemCard';
import { DesktopOnly, MobileOnly } from '../../components/Layout';
import { PageHeading } from '../../components/PageHeading';
import { SectionHeading } from '../../components/SectionHeading';
import { SideImage } from '../../components/SideImage';
import { SplitLayout } from '../../composites/SplitLayout';
import { layout, space } from '../../theme/tokens';
import type { HomeTemplateProps } from './HomeTemplate.types';

const HERO_WIDTH = '45%';
const CATEGORY_ASPECT = { desktop: '3 / 2', mobile: '6 / 7' };
const COLLECTION_ASPECT = { desktop: '5 / 3', mobile: '5 / 7' };

export function HomeTemplate({
  propertyName,
  propertyLogoUrl,
  heroImageUrl,
  heading = 'What are you looking for?',
  categories,
  onSelectCategory,
  featuredLink,
  collections,
  onSelectCollection,
  collectionsHeading = 'Browse collections',
  noImageLabel,
}: HomeTemplateProps) {
  const visibleCollections = collections ?? [];
  const brandHeader = <BrandHeader propertyName={propertyName} logoUrl={propertyLogoUrl} />;

  return (
    <>
      <MobileOnly>{brandHeader}</MobileOnly>
      <SplitLayout
        leftWidth={HERO_WIDTH}
        contentMaxWidth="none"
        height="var(--layout-viewport-height, 100vh)"
        contentGap={space[7]}
        mobileHorizontalPadding={space[5]}
        left={
          <SideImage
            src={heroImageUrl ?? undefined}
            alt=""
            sticky
            mobileHeight={layout.heroImageMobileHeight}
          />
        }
        right={
          <>
            <DesktopOnly>{brandHeader}</DesktopOnly>
            <PageHeading title={heading} flush />
            <CardGrid
              $mobileColumns={2}
              $flushBottom
              $columnGapMobile={space[3]}
              $rowGapMobile={space[4]}
              $columnGapTablet={space[4]}
              $rowGapTablet={space['20px']}
            >
              {categories.map((cat) => (
                <ItemCard
                  key={cat.id}
                  image={{
                    src: cat.imageUrl || undefined,
                    alt: cat.label,
                    placeholderLabel: noImageLabel,
                  }}
                  imageAspectRatio={CATEGORY_ASPECT.desktop}
                  imageMobileAspectRatio={CATEGORY_ASPECT.mobile}
                  textPosition="center"
                  title={cat.label}
                  ariaLabel={cat.label}
                  onSelect={() => onSelectCategory(cat.id)}
                  boldTitle
                  imageOverlay={cat.imageOverlay}
                />
              ))}
            </CardGrid>
            {featuredLink && (
              <FeaturedBanner
                title={featuredLink.title}
                subtitle={featuredLink.subtitle}
                imageUrl={featuredLink.imageUrl}
                imageAlt={featuredLink.title}
                ctaLabel={featuredLink.ctaLabel}
                onCtaClick={featuredLink.onCtaClick}
                noImageLabel={noImageLabel}
              />
            )}
            {visibleCollections.length > 0 && (
              <SectionHeading title={collectionsHeading}>
                <CardGrid
                  $mobileColumns={2}
                  $flushBottom
                  $columnGapMobile={space[3]}
                  $rowGapMobile={space[4]}
                  $columnGapTablet={space[4]}
                  $rowGapTablet={space['20px']}
                >
                  {visibleCollections.map((collection) => (
                    <ItemCard
                      key={collection.id}
                      variant="overlay"
                      image={{
                        src: collection.imageUrl || undefined,
                        alt: collection.label,
                        placeholderLabel: noImageLabel,
                      }}
                      imageAspectRatio={COLLECTION_ASPECT.desktop}
                      imageMobileAspectRatio={COLLECTION_ASPECT.mobile}
                      title={collection.label}
                      ariaLabel={collection.label}
                      onSelect={
                        onSelectCollection ? () => onSelectCollection(collection.id) : undefined
                      }
                    />
                  ))}
                </CardGrid>
              </SectionHeading>
            )}
          </>
        }
      />
    </>
  );
}
